// Shared plumbing for the list, detail and editor pages: the product's
// adapter from the URL, loading one policy, and the on/off and delete actions
// with their confirmations (spec 7.6).
import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";

import { getAdapter } from "./productRegistry";
import { isPolicyProduct, policiesPath } from "./policyProducts";
import { getPolicyFlags } from "./policyFlags";

const noData = () => undefined;

/**
 * Renders `render({ product, adapter, flags })` for a product that has an
 * adapter. An unknown product goes to Identity.
 */
export function ProductGate({ render }) {
  const { product } = useParams();
  if (!isPolicyProduct(product)) return <Navigate to={policiesPath("ad")} replace />;
  const adapter = getAdapter(product);
  if (!adapter) return <Navigate to={policiesPath("ad")} replace />;
  return render({ product, adapter, flags: getPolicyFlags() });
}

/** The adapter's useData hook, or nothing. Call unconditionally at the top of a page. */
export function useAdapterData(adapter, draft, setProduct) {
  const hook = adapter.useData || noData;
  return hook(draft, setProduct);
}

/** One stored policy by id. initial: a row already in hand (router state). */
export function usePolicyRow(adapter, policyId, initial) {
  const [state, setState] = useState(() => ({
    row: initial && String(adapter.rowId(initial)) === String(policyId) ? initial : null,
    loading: !!policyId,
    error: null,
  }));
  const load = useCallback(() => {
    if (!policyId) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    Promise.resolve(adapter.get(policyId))
      .then((row) => setState({ row: row || null, loading: false, error: row ? null : "notFound" }))
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error("Policy load failed", e?.raw ?? e);
        setState((s) => ({ ...s, loading: false, error: "failed" }));
      });
  }, [adapter, policyId]);
  useEffect(() => {
    load();
  }, [load]);
  return { ...state, reload: load, setRow: (row) => setState((s) => ({ ...s, row })) };
}

/**
 * Switch on/off and delete, with the confirmations. after(kind, row) runs once
 * the call succeeded.
 */
export function usePolicyActions(adapter, { messageApi, modalApi, after }) {
  const subject = adapter.labels?.subject || "sign-in";
  const setActive = (row, active) => {
    const run = () =>
      Promise.resolve(adapter.setActive(row, active))
        .then(() => {
          messageApi.success(active ? `${row.policyName} is on` : `${row.policyName} is off`);
          if (after) after(active ? "on" : "off", row);
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.error("Policy on/off failed", e?.raw ?? e);
          messageApi.error(active ? "Couldn't switch the policy on. Try again." : "Couldn't switch the policy off. Try again.");
        });
    const flow = adapter.actionOf(row.policyJson || {});
    if (!active && flow === "block") {
      modalApi.confirm({
        title: `Switch off “${row.policyName}”?`,
        content: `${subject.charAt(0).toUpperCase()}${subject.slice(1)}s it blocks will be allowed or challenged by other policies.`,
        okText: "Switch off",
        okButtonProps: { danger: true },
        onOk: run,
      });
      return;
    }
    run();
  };
  const remove = (row) => {
    modalApi.confirm({
      title: `Delete “${row.policyName}”?`,
      content: "The policy stops applying straight away. This can't be undone.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: () =>
        Promise.resolve(adapter.remove(row))
          .then(() => {
            messageApi.success(`${row.policyName} deleted`);
            if (after) after("deleted", row);
          })
          .catch((e) => {
            // eslint-disable-next-line no-console
            console.error("Policy delete failed", e?.raw ?? e);
            messageApi.error("Couldn't delete the policy. Try again.");
          }),
    });
  };
  return { setActive, remove };
}

/** window.matchMedia as state. Without matchMedia (tests), `fallback`. */
export function useMediaQuery(query, fallback = true) {
  const get = () => (typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(query).matches : fallback);
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mql = window.matchMedia(query);
    const on = () => setMatches(mql.matches);
    on();
    if (mql.addEventListener) mql.addEventListener("change", on);
    else if (mql.addListener) mql.addListener(on);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", on);
      else if (mql.removeListener) mql.removeListener(on);
    };
  }, [query]);
  return matches;
}
